import { effect, Injectable, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import {
  CreateSparkCampaignResponseDto,
  GetSparkCampaignForBriefAdGroupDto,
  SparkAdBudgetMode,
  TiktokAdGroupLanguageOption,
  TiktokAdGroupLocationOption,
  TiktokMarketingApiEnvelope,
} from '@/app/common/general-types/tiktok-spark-ads.dto';
import { TiktokSparkAdsService } from '@/app/services/tiktok-spark-ads/tiktok-spark-ads.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { TtoCampaignVideoDto } from '@/app/common/general-types/tiktok-one-reporting.dto';

/** `YYYY-MM-DDTHH:mm` in local time for `<input type="datetime-local">`. */
function formatDateTimeLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function defaultAdGroupScheduleStartDateTimeLocal(): string {
  return formatDateTimeLocal(new Date());
}

function defaultAdGroupScheduleEndDateTimeLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return formatDateTimeLocal(d);
}

function pickScalarString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined || v === null) {
      continue;
    }
    const s = typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';
    if (s.length > 0) {
      return s;
    }
  }
  return undefined;
}

/** Nested regions from TikTok tool/region use the same `region_info` array shape as the root payload. */
function pickChildRegionArrays(obj: Record<string, unknown>): unknown[] | undefined {
  const regionInfo = obj['region_info'] ?? obj['regionInfo'];
  if (Array.isArray(regionInfo) && regionInfo.length > 0) {
    return regionInfo;
  }
  const keys = [
    'sub_regions',
    'sub_region',
    'regions',
    'children',
    'subordinate_regions',
    'subordinate',
    'child_regions',
  ];
  for (const k of keys) {
    const v = obj[k];
    if (Array.isArray(v) && v.length > 0) {
      return v;
    }
    if (v !== null && typeof v === 'object' && !Array.isArray(v) && (k === 'sub_region' || k === 'sub_regions')) {
      return [v];
    }
  }
  return undefined;
}

/** When TikTok omits our known id keys, pick a numeric id from the object (location ids are digit strings). */
function inferLocationIdFromRecord(r: Record<string, unknown>): string | undefined {
  const scored: { score: number; value: string }[] = [];
  for (const [k, v] of Object.entries(r)) {
    if (v === null || v === undefined) {
      continue;
    }
    let s: string | undefined;
    if (typeof v === 'number' && Number.isFinite(v)) {
      s = String(Math.trunc(v));
    } else if (typeof v === 'string' && /^\d+$/.test(v.trim())) {
      s = v.trim();
    }
    if (!s || s.length < 2) {
      continue;
    }
    const kl = k.toLowerCase();
    let score = s.length;
    if (/location|region|geo|area|geoname|admin|place/.test(kl) && /id|code|key/.test(kl)) {
      score += 100;
    }
    if (kl === 'id' || kl.endsWith('_id') || kl.endsWith('id')) {
      score += 50;
    }
    scored.push({ score, value: s });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.value;
}

/**
 * TikTok tool/region responses use mixed shapes (snake_case, camelCase, region_name vs name, etc.).
 * Normalize so UI always gets `location_id` + `name` + optional `sub_regions`.
 */
function normalizeTiktokRegionNode(raw: unknown): TiktokAdGroupLocationOption | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const locationId =
    pickScalarString(r, [
      'location_id',
      'locationId',
      'id',
      'region_id',
      'regionId',
      'value',
      'geo_id',
      'geoId',
      'location_code',
      'locationCode',
      'area_id',
      'areaId',
      'key',
      'geoname_id',
      'geonameId',
    ]) ?? inferLocationIdFromRecord(r);
  if (locationId === undefined) {
    return null;
  }

  const name =
    pickScalarString(r, [
      'name',
      'region_name',
      'regionName',
      'location_name',
      'locationName',
      'title',
      'label',
      'text',
      'geo_name',
      'geoName',
      'region',
      'english_name',
      'englishName',
      'localized_name',
      'localizedName',
      'display_name',
      'displayName',
    ]) ?? locationId;

  const level = pickScalarString(r, ['level', 'region_type', 'regionType', 'type', 'location_type', 'locationType']);

  const parent_id = pickScalarString(r, ['parent_id', 'parentId', 'father_id', 'fatherId']);

  const childrenRaw = pickChildRegionArrays(r);
  let sub_regions: TiktokAdGroupLocationOption[] | undefined;
  if (childrenRaw) {
    sub_regions = childrenRaw
      .map((c) => normalizeTiktokRegionNode(c))
      .filter((c): c is TiktokAdGroupLocationOption => c !== null);
    if (sub_regions.length === 0) {
      sub_regions = undefined;
    }
  }

  const ccRaw = pickScalarString(r, [
    'country_code',
    'countryCode',
    'iso_code',
    'isoCode',
    'iso3166_alpha2',
    'iso_3166_alpha2',
    'two_letter_country_code',
    'twoLetterCountryCode',
  ]);
  const country_code =
    ccRaw && /^[a-z]{2}$/i.test(ccRaw.trim()) ? ccRaw.trim().toUpperCase() : undefined;

  return {
    location_id: locationId,
    name,
    level,
    parent_id,
    sub_regions,
    ...(country_code !== undefined ? { country_code } : {}),
  };
}

/**
 * TikTok GET /open_api/v1.3/tool/region/ returns `data: { region_info: [...] }`.
 * Use that array only (arrays of objects); do not coerce plain objects into lists.
 */
function extractRawRegionList(data: unknown): unknown[] {
  if (data === null || data === undefined) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data !== 'object') {
    return [];
  }
  const record = data as Record<string, unknown>;
  const regionInfo = record['region_info'] ?? record['regionInfo'];
  if (Array.isArray(regionInfo)) {
    return regionInfo;
  }
  const fallbacks = ['regions', 'region_list', 'regionList', 'location_list', 'locationList'] as const;
  for (const k of fallbacks) {
    const v = record[k];
    if (Array.isArray(v)) {
      return v;
    }
  }
  return [];
}

function regionsFromToolRegionData(data: unknown): TiktokAdGroupLocationOption[] {
  let root: unknown = data;
  if (root !== null && typeof root === 'object' && !Array.isArray(root)) {
    const rec = root as Record<string, unknown>;
    if (typeof rec['code'] === 'number' && rec['data'] !== undefined) {
      root = rec['data'];
    }
  }

  const raw = extractRawRegionList(root);
  return raw
    .map((item) => normalizeTiktokRegionNode(item))
    .filter((n): n is TiktokAdGroupLocationOption => n !== null);
}

function extractRawLanguageList(data: unknown): unknown[] {
  if (data === null || data === undefined) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data !== 'object') {
    return [];
  }
  const record = data as Record<string, unknown>;
  const keys = [
    'language_infos',
    'languageInfos',
    'languages',
    'language_list',
    'languageList',
    'list',
  ] as const;
  for (const k of keys) {
    const v = record[k];
    if (Array.isArray(v)) {
      return v;
    }
  }
  return [];
}

function normalizeTiktokLanguageNode(raw: unknown): TiktokAdGroupLanguageOption | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const code =
    pickScalarString(r, [
      'language_id',
      'languageId',
      'language_code',
      'languageCode',
      'code',
      'id',
      'value',
    ]) ?? undefined;
  if (code === undefined) {
    return null;
  }
  const name =
    pickScalarString(r, [
      'language',
      'language_name',
      'languageName',
      'name',
      'display_name',
      'displayName',
    ]) ?? code;
  return { code, name };
}

function languagesFromToolLanguageData(data: unknown): TiktokAdGroupLanguageOption[] {
  let root: unknown = data;
  if (root !== null && typeof root === 'object' && !Array.isArray(root)) {
    const rec = root as Record<string, unknown>;
    if (typeof rec['code'] === 'number' && rec['data'] !== undefined) {
      root = rec['data'];
    }
  }

  const raw = extractRawLanguageList(root);
  const normalized = raw
    .map((item) => normalizeTiktokLanguageNode(item))
    .filter((n): n is TiktokAdGroupLanguageOption => n !== null);
  const seen = new Set<string>();
  const deduped: TiktokAdGroupLanguageOption[] = [];
  for (const n of normalized) {
    if (seen.has(n.code)) {
      continue;
    }
    seen.add(n.code);
    deduped.push(n);
  }
  deduped.sort((a, b) => a.name.localeCompare(b.name));
  return deduped;
}

/** Single pass while loading — avoids UI recomputing a full-tree walk on every read. */
function indexLocationLabels(nodes: TiktokAdGroupLocationOption[] | undefined, map: Map<string, string>): void {
  if (!nodes) {
    return;
  }
  for (const n of nodes) {
    map.set(n.location_id, (n.name ?? '').trim() || String(n.location_id));
    indexLocationLabels(n.sub_regions, map);
  }
}

/** Map ISO 3166-1 alpha-2 → English name hints for TikTok country labels (exact or substring for long hints). */
const ISO2_TO_REGION_NAME_HINTS: Readonly<Record<string, readonly string[]>> = {
  GB: ['united kingdom', 'great britain', 'northern ireland', 'scotland', 'wales', 'england', 'uk'],
  US: ['united states', 'u.s.a.', 'usa'],
  AU: ['australia'],
  CA: ['canada'],
  NZ: ['new zealand'],
  IE: ['ireland'],
  DE: ['germany', 'deutschland'],
  FR: ['france'],
  ES: ['spain'],
  IT: ['italy'],
  NL: ['netherlands'],
  BE: ['belgium'],
  AT: ['austria'],
  CH: ['switzerland'],
  PT: ['portugal'],
  SE: ['sweden'],
  NO: ['norway'],
  DK: ['denmark'],
  FI: ['finland'],
  PL: ['poland'],
  IN: ['india'],
  JP: ['japan'],
  KR: ['south korea', 'korea, republic', 'republic of korea', 'korea (south)'],
  SG: ['singapore'],
  MY: ['malaysia'],
  TH: ['thailand'],
  PH: ['philippines'],
  ID: ['indonesia'],
  VN: ['vietnam'],
  BR: ['brazil'],
  MX: ['mexico'],
  AR: ['argentina'],
  CL: ['chile'],
  CO: ['colombia'],
  ZA: ['south africa'],
  AE: ['united arab emirates'],
  SA: ['saudi arabia'],
  IL: ['israel'],
  CN: ['china'],
  HK: ['hong kong'],
  TW: ['taiwan'],
  CZ: ['czech'],
  HU: ['hungary'],
  RO: ['romania'],
  GR: ['greece'],
  TR: ['türkiye', 'turkey'],
};

const CURRENCY_TO_ISO2: Readonly<Record<string, string>> = {
  GBP: 'GB',
  USD: 'US',
  EUR: 'DE',
  AUD: 'AU',
  CAD: 'CA',
  NZD: 'NZ',
  SEK: 'SE',
  NOK: 'NO',
  DKK: 'DK',
  CHF: 'CH',
  PLN: 'PL',
  JPY: 'JP',
  INR: 'IN',
  BRL: 'BR',
  MXN: 'MX',
  SGD: 'SG',
  HKD: 'HK',
  CNY: 'CN',
};

/** TikTok ad-group optimization_goal values exposed in the Spark ad-group UI. */
export const SPARK_AD_GROUP_OPTIMIZATION_GOALS = [
  'CLICK',
  'CONVERT',
  'INSTALL',
  'REACH',
  'LEAD_GENERATION',
  'FOLLOWERS',
  'PAGE_VISIT',
  'ENGAGED_VIEW',
  'TRAFFIC_LANDING_PAGE_VIEW',
] as const;

export type SparkAdGroupUiOptimizationGoal = (typeof SPARK_AD_GROUP_OPTIMIZATION_GOALS)[number];

/** UI goals plus API-only values (not shown in Spark ad-group goal picker). */
export type SparkAdGroupOptimizationGoal = SparkAdGroupUiOptimizationGoal | 'IN_APP_EVENT' | 'VALUE';

export type SparkAdGroupBillingEventForOptimization = 'CPC' | 'CPM' | 'CPV' | 'OCPM';

/** Maps UI optimization_goal values to TikTok `billing_event`. */
export const OPTIMIZATION_GOAL_TO_BILLING_EVENT_MAP: Record<
  SparkAdGroupOptimizationGoal,
  SparkAdGroupBillingEventForOptimization
> = {
  CLICK: 'CPC',
  CONVERT: 'OCPM',
  INSTALL: 'OCPM',
  REACH: 'CPM',
  LEAD_GENERATION: 'OCPM',
  FOLLOWERS: 'OCPM',
  PAGE_VISIT: 'CPC',
  ENGAGED_VIEW: 'CPV',
  TRAFFIC_LANDING_PAGE_VIEW: 'OCPM',
  IN_APP_EVENT: 'OCPM',
  VALUE: 'OCPM',
};

/**
 * TikTok expects `optimization_event` when a pixel is attached, or for IN_APP_EVENT / VALUE
 * goals without a pixel (callers supply the concrete event string).
 */
export function optimizationEventRequired(
  pixelId: string | undefined,
  goal: SparkAdGroupOptimizationGoal,
): boolean {
  const pid = pixelId?.trim() ?? '';
  if (pid.length > 0) {
    return true;
  }
  return goal === 'IN_APP_EVENT' || goal === 'VALUE';
}

/** Goals allowed for the Spark flow, filtered by campaign `objectiveType`. */
export function optimizationGoalsForObjective(
  objectiveType: string | undefined,
): readonly SparkAdGroupUiOptimizationGoal[] {
  const base: SparkAdGroupUiOptimizationGoal[] = [
    'CLICK',
    'CONVERT',
    'INSTALL',
    'REACH',
    'LEAD_GENERATION',
    'FOLLOWERS',
    'ENGAGED_VIEW',
  ];
  const out: SparkAdGroupUiOptimizationGoal[] = [...base];
  if (objectiveType === 'ENGAGEMENT') {
    out.push('PAGE_VISIT');
  }
  if (objectiveType === 'TRAFFIC') {
    out.push('TRAFFIC_LANDING_PAGE_VIEW');
  }
  return out;
}

/**
 * Default ad-group `optimization_goal` for a TikTok campaign `objectiveType`, when the preferred
 * value is allowed for that objective; otherwise the first allowed UI goal (typically CLICK).
 */
export function defaultAdGroupOptimizationGoalForObjective(
  objectiveType: string | undefined,
): SparkAdGroupUiOptimizationGoal {
  const allowed = optimizationGoalsForObjective(objectiveType);
  if (allowed.length === 0) {
    return 'CLICK';
  }

  const preference: Partial<Record<string, SparkAdGroupUiOptimizationGoal>> = {
    REACH: 'REACH',
    LEAD_GENERATION: 'LEAD_GENERATION',
    TRAFFIC: 'TRAFFIC_LANDING_PAGE_VIEW',
    ENGAGEMENT: 'PAGE_VISIT',
    APP_PROMOTION: 'INSTALL',
    VIDEO_VIEWS: 'ENGAGED_VIEW',
    WEB_CONVERSIONS: 'CONVERT',
  };

  const preferred = objectiveType !== undefined ? preference[objectiveType] : undefined;
  if (preferred !== undefined && allowed.includes(preferred)) {
    return preferred;
  }
  return allowed[0] ?? 'CLICK';
}

function extractBrandCountryIso(brand: unknown): string | undefined {
  if (brand === null || typeof brand !== 'object') {
    return undefined;
  }
  const b = brand as Record<string, unknown>;
  for (const k of ['countryCode', 'country_code', 'registeredCountryCode', 'registered_country_code']) {
    const v = b[k];
    if (typeof v === 'string' && /^[a-z]{2}$/i.test(v.trim())) {
      return v.trim().toUpperCase();
    }
  }
  const cur = b['currencyCode'] ?? b['currency_code'];
  if (typeof cur === 'string' && cur.trim() !== '') {
    const iso = CURRENCY_TO_ISO2[cur.trim().toUpperCase()];
    if (iso !== undefined) {
      return iso;
    }
  }
  return undefined;
}

function isLikelyCountryLevel(level: string | undefined): boolean {
  const l = (level ?? '').trim().toUpperCase();
  if (l === '') {
    return true;
  }
  return l === 'COUNTRY' || l === 'COUNTRIES';
}

function regionMatchesIsoHints(node: TiktokAdGroupLocationOption, iso: string): boolean {
  const u = iso.trim().toUpperCase();
  const hints = ISO2_TO_REGION_NAME_HINTS[u];
  const label = (node.name ?? '').trim().toLowerCase();
  if (!label || !hints) {
    return false;
  }
  for (const h of hints) {
    const hint = h.trim().toLowerCase();
    if (hint !== '' && label === hint) {
      return true;
    }
  }
  for (const h of hints) {
    const hint = h.trim().toLowerCase();
    if (hint.length >= 6 && label.includes(hint)) {
      return true;
    }
  }
  return false;
}

/** Match TikTok region tree row to brand ISO: prefer API `country_code`, else name hints on country-level nodes. */
function findRegionForBrandCountry(
  nodes: TiktokAdGroupLocationOption[],
  iso: string,
): TiktokAdGroupLocationOption | undefined {
  const u = iso.trim().toUpperCase();
  if (u.length !== 2) {
    return undefined;
  }

  const walkExactCode = (list: TiktokAdGroupLocationOption[]): TiktokAdGroupLocationOption | undefined => {
    for (const n of list) {
      const cc = n.country_code?.trim().toUpperCase();
      if (cc === u && isLikelyCountryLevel(n.level)) {
        return n;
      }
      if (n.sub_regions?.length) {
        const found = walkExactCode(n.sub_regions);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return undefined;
  };

  const byCode = walkExactCode(nodes);
  if (byCode !== undefined) {
    return byCode;
  }

  const walkHints = (list: TiktokAdGroupLocationOption[]): TiktokAdGroupLocationOption | undefined => {
    for (const n of list) {
      if (isLikelyCountryLevel(n.level) && regionMatchesIsoHints(n, u)) {
        return n;
      }
      if (n.sub_regions?.length) {
        const found = walkHints(n.sub_regions);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return undefined;
  };

  return walkHints(nodes);
}

@Injectable({
  providedIn: 'root',
})
export class CreateSparkCampaign {
  private tiktokSparkAdsService = inject(TiktokSparkAdsService);
  private currentUserService = inject(CurrentUserService);

  /** Seeds ad-group optimization defaults once per focused campaign (create-ad-group flow). */
  private lastSeededAdGroupCampaignId = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      const campaign = this.currentSparkCampaign();
      const campaignId = campaign?.id;
      const objectiveType = campaign?.objectiveType;

      if (campaignId !== undefined && this.lastSeededAdGroupCampaignId() !== campaignId) {
        this.lastSeededAdGroupCampaignId.set(campaignId);
        const defaultGoal = defaultAdGroupOptimizationGoalForObjective(objectiveType);
        this.applyOptimizationGoal(defaultGoal, objectiveType);
        return;
      }

      const allowed = optimizationGoalsForObjective(objectiveType);
      const allowedSet = new Set(allowed);
      const current = this.optimization_goal();
      const isExtendedApiGoal = current === 'IN_APP_EVENT' || current === 'VALUE';
      if (!allowedSet.has(current as SparkAdGroupUiOptimizationGoal) && !isExtendedApiGoal) {
        const first = allowed[0];
        if (first !== undefined) {
          this.applyOptimizationGoal(first, objectiveType);
        }
        return;
      }
      const goal = current as SparkAdGroupOptimizationGoal;
      const expected = OPTIMIZATION_GOAL_TO_BILLING_EVENT_MAP[goal];
      if (this.billing_event() !== expected) {
        this.applyOptimizationGoal(goal, objectiveType);
      }
    });

    effect(() => {
      const be = this.billing_event();
      if (
        this.bid_type() === 'BID_TYPE_CUSTOM' &&
        be !== 'CPC' &&
        be !== 'CPM' &&
        be !== 'CPV'
      ) {
        this.bid_type.set('BID_TYPE_NO_BID');
      }
    });
  }

  /**
   * Sets optimization_goal, billing_event, and any TikTok-required companion fields
   * (INSTALL app bundle placement, TRAFFIC landing page view + website promotion, etc.).
   * Respecting campaign objective is the caller’s responsibility; invalid goals are ignored.
   */
  applyOptimizationGoal(goal: SparkAdGroupOptimizationGoal, objectiveType: string | undefined): void {
    const allowed = optimizationGoalsForObjective(objectiveType);
    const permissible =
      goal === 'IN_APP_EVENT' ||
      goal === 'VALUE' ||
      allowed.includes(goal as SparkAdGroupUiOptimizationGoal);
    if (!permissible) {
      return;
    }
    const previous = this.optimization_goal();
    this.optimization_goal.set(goal);
    this.billing_event.set(OPTIMIZATION_GOAL_TO_BILLING_EVENT_MAP[goal]);

    if (goal === 'INSTALL') {
      this.secondary_optimization_event.set('ACTIVE');
      this.placements.set(['PLACEMENT_GLOBAL_APP_BUNDLE']);
      this.optimization_event.set(undefined);
      this.promotion_type.set(undefined);
      return;
    }

    if (goal === 'TRAFFIC_LANDING_PAGE_VIEW') {
      this.secondary_optimization_event.set(undefined);
      this.optimization_event.set('LANDING_PAGE_VIEW');
      this.promotion_type.set('WEBSITE');
      this.placement_type.set('PLACEMENT_TYPE_AUTOMATIC');
      this.placements.update((p) => {
        const filtered = p.filter((id) => id !== 'PLACEMENT_GLOBAL_APP_BUNDLE');
        return filtered.length > 0 ? filtered : ['PLACEMENT_TIKTOK'];
      });
      return;
    }

    if (goal === 'PAGE_VISIT') {
      this.secondary_optimization_event.set(undefined);
      this.optimization_event.set('PAGE_VISIT');
      this.promotion_type.set(undefined);
      if (previous === 'TRAFFIC_LANDING_PAGE_VIEW' || previous === 'INSTALL') {
        this.placement_type.set('PLACEMENT_TYPE_NORMAL');
        this.placements.set(['PLACEMENT_TIKTOK']);
      }
      return;
    }

    this.secondary_optimization_event.set(undefined);
    const hadTrafficLandingArtifacts =
      this.optimization_event() === 'LANDING_PAGE_VIEW' && this.promotion_type() === 'WEBSITE';
    const pixelId = this.pixel_id();
    if (!optimizationEventRequired(pixelId, goal)) {
      this.optimization_event.set(undefined);
    }
    this.promotion_type.set(undefined);

    if (
      previous === 'TRAFFIC_LANDING_PAGE_VIEW' ||
      previous === 'INSTALL' ||
      hadTrafficLandingArtifacts
    ) {
      this.placement_type.set('PLACEMENT_TYPE_NORMAL');
      this.placements.set(['PLACEMENT_TIKTOK']);
    }
  }

  currentSparkCampaign = signal<CreateSparkCampaignResponseDto | undefined>(undefined);
  public currentSparkAdGroup = signal<GetSparkCampaignForBriefAdGroupDto | undefined>(undefined);


  adGroupLocations = signal<TiktokAdGroupLocationOption[] | undefined>(undefined);

  /** Built once when `adGroupLocations` loads (flat id → display label). */
  locationLabelsById = signal<ReadonlyMap<string, string>>(new Map());

  /** Options from TikTok `GET /tool/language/` (undefined = not loaded yet). */
  adGroupLanguageOptions = signal<readonly TiktokAdGroupLanguageOption[] | undefined>(undefined);

  /** Built when `adGroupLanguageOptions` loads (code → display label). */
  languageLabelsByCode = signal<ReadonlyMap<string, string>>(new Map());

  /** Campaign Values */
  campaignName = signal<string | undefined>(undefined);
  selectedAdvertisingObjective = signal<{ objective: string; api_value: string } | undefined>(undefined);
  selectedBudgetMode = signal<SparkAdBudgetMode>('BUDGET_MODE_DAY');
  budgetOptimizeOn = signal<boolean>(false);
  budget = signal<number>(50);
  selectedAdvertiserAccountId = signal<string | undefined>(undefined);
  /** Campaign Values - END*/

  /** Ad-Group Values */
  adGroupName = signal<string | undefined>(undefined);
  // Placements
  placements = signal<string[]>(["PLACEMENT_TIKTOK"]);
  placement_type = signal<'PLACEMENT_TYPE_NORMAL' | 'PLACEMENT_TYPE_AUTOMATIC'>('PLACEMENT_TYPE_NORMAL');
  comment_disabled = signal<boolean>(false);
  video_download_disabled = signal<boolean>(false);
  share_disabled = signal<boolean>(false);

  // Targeting
  // Targeting - Demographic
  location_ids = signal<string[]>([]);
  is_hfss = signal<boolean>(false);
  age = signal<('AGE_18_24' | 'AGE_25_34' | 'AGE_35_44' | 'AGE_45_54' | 'AGE_55_100')[]>([
    'AGE_18_24',
    'AGE_25_34',
    'AGE_35_44',
    'AGE_45_54',
    'AGE_55_100',
  ]);
  gender = signal<'GENDER_FEMALE' | 'GENDER_MALE' | 'GENDER_UNLIMITED'>('GENDER_UNLIMITED');
  languages = signal<string[]>([]);
  // Targeting - Detailed
  spending_power = signal<'ALL' | 'HIGH'>('ALL');
  smart_interest_behavior_enabled = signal<boolean>(false);
  // Targeting - Device
  operating_systems = signal<('ANDROID' | 'IOS')[]>(['ANDROID', 'IOS']);

  // Brand Safety & Suitability
  // Budget & Schedule
  selectedBudgetModeAdGroup = signal<SparkAdBudgetMode>('BUDGET_MODE_DAY');
  budgetAdGroup = signal<number>(20);
  startDateAdGroup = signal<string | undefined>(defaultAdGroupScheduleStartDateTimeLocal());
  endDateAdGroup = signal<string | undefined>(defaultAdGroupScheduleEndDateTimeLocal());
  // Bidding & Optimization

  optimization_goal = signal<SparkAdGroupOptimizationGoal>('CLICK');
  billing_event = signal<'CPC' | 'CPM' | 'CPV' | 'GD' | 'OCPM' | undefined>(undefined);
  secondary_optimization_event = signal<string | undefined>(undefined);

  frequency = signal<number | undefined>(3);
  frequency_schedule = signal<number | undefined>(1);
  /** 
   * Frequency schedule, together with frequency, controls how often people see your ad (only available for REACH ads). 
   */
  bid_price = signal<number>(0);
  bid_type = signal<'BID_TYPE_CUSTOM' | 'BID_TYPE_NO_BID'>('BID_TYPE_NO_BID');

  optimization_event = signal<string | undefined>(undefined);
  promotion_type = signal<string | undefined>(undefined);
  /** TikTok pixel for web conversion / attribution (when set, `optimization_event` is required for API). */
  pixel_id = signal<string | undefined>(undefined);
  /** Ad-Group Values - END */



  /** Ad Values */
  adName = signal<string | undefined>(undefined);
  selectedCreative = signal<TtoCampaignVideoDto | undefined>(undefined);

  // Details
  call_to_action_active = signal<boolean>(false);
  call_to_action = signal<string | undefined>("LEARN_MORE");

  ad_format = signal<"SINGLE_VIDEO">("SINGLE_VIDEO");
  identity_type = signal<"CUSTOMIZED_USER" | "AUTH_CODE" |"TT_USER" | "BC_AUTH_TT">("AUTH_CODE")
  video_id = signal<string | undefined>(undefined); // Not required when identity_type is not "AUTH_CODE" -> Which we shall use.
  ad_text = signal<string | undefined>(undefined);
  // Details - Destination
  landing_page_url = signal<string | undefined>(undefined);
  enable_disclaimer = signal<boolean>(false);
  disclaimer_type = signal<"TEXT_ONLY" | "TEXT_LINK" | undefined>(undefined);
  disclaimer_text = signal<string | undefined>(undefined);
  // Tracking
  impression_tracking_url = signal<string | undefined>(undefined);
  click_tracking_url = signal<string | undefined>(undefined);
  /** Ad Values - END */

  /**
   * Loads valid location IDs for ad group targeting from TikTok Marketing API v1.3 `GET /tool/region/`.
   * Requires `briefId`, a resolved campaign (`objectiveType`), and an advertiser id (selected or from campaign).
   * @see https://business-api.tiktok.com/portal/docs?id=1737189539571713
   */
  loadLocations(options: {
    briefId: string;
    placements?: string[];
    /** TikTok `level_range`: ALL | TO_COUNTRY | TO_PROVINCE | TO_CITY | TO_DISTRICT */
    levelRange?: string;
  }): void {
    const advertiserId =
      this.selectedAdvertiserAccountId() ?? this.currentSparkCampaign()?.advertiserId;
    /**
     * Fall back to the objective chosen in the wizard so markets can be loaded before
     * the campaign exists — otherwise a first-run brief has nothing to target with.
     */
    const objectiveType =
      this.currentSparkCampaign()?.objectiveType ??
      this.selectedAdvertisingObjective()?.api_value;
    if (!advertiserId || !objectiveType || !options.briefId) {
      this.adGroupLocations.set(undefined);
      this.locationLabelsById.set(new Map());
      return;
    }

    this.tiktokSparkAdsService
      .getToolRegion({
        briefId: options.briefId,
        advertiserId,
        objectiveType,
        placements: options.placements,
        levelRange: options.levelRange,
      })
      .pipe(take(1))
      .subscribe({
        next: (res: TiktokMarketingApiEnvelope) => {
          console.log('loadLocations() ---> res: ', res);
          const r = res as TiktokMarketingApiEnvelope & { data?: unknown };
          if (typeof r.code === 'number' && r.code !== 0) {
            this.adGroupLocations.set([]);
            this.locationLabelsById.set(new Map());
            return;
          }
          const payload =
            typeof r.code === 'number' && r.data !== undefined ? r.data : (res as unknown);
          const regions = regionsFromToolRegionData(payload);
          const labelMap = new Map<string, string>();
          indexLocationLabels(regions, labelMap);
          this.locationLabelsById.set(labelMap);
          this.adGroupLocations.set(regions);
          if (this.location_ids().length === 0 && regions.length > 0) {
            const iso = extractBrandCountryIso(this.currentUserService.focussedBrand);
            if (iso !== undefined) {
              const node = findRegionForBrandCountry(regions, iso);
              if (node !== undefined) {
                this.location_ids.set([node.location_id]);
              }
            }
          }
        },
        error: () => {
          this.adGroupLocations.set(undefined);
          this.locationLabelsById.set(new Map());
        },
      });
  }

  /**
   * Loads valid language codes for ad group targeting from TikTok Marketing API v1.3 `GET /tool/language/`.
   * Empty `languages` in state means “all languages” (TikTok default).
   * @see https://business-api.tiktok.com/portal/docs/get-languages/v1.3
   */
  loadLanguages(options: { briefId: string }): void {
    const advertiserId =
      this.selectedAdvertiserAccountId() ?? this.currentSparkCampaign()?.advertiserId;
    if (!advertiserId || !options.briefId) {
      this.adGroupLanguageOptions.set(undefined);
      this.languageLabelsByCode.set(new Map());
      return;
    }

    this.tiktokSparkAdsService
      .getToolLanguage({
        briefId: options.briefId,
        advertiserId,
      })
      .pipe(take(1))
      .subscribe({
        next: (res: TiktokMarketingApiEnvelope) => {
          const r = res as TiktokMarketingApiEnvelope & { data?: unknown };
          if (typeof r.code === 'number' && r.code !== 0) {
            this.adGroupLanguageOptions.set([]);
            this.languageLabelsByCode.set(new Map());
            return;
          }
          const payload =
            typeof r.code === 'number' && r.data !== undefined ? r.data : (res as unknown);
          const langs = languagesFromToolLanguageData(payload);
          const labelMap = new Map<string, string>();
          for (const l of langs) {
            labelMap.set(l.code, (l.name ?? '').trim() || l.code);
          }
          this.languageLabelsByCode.set(labelMap);
          this.adGroupLanguageOptions.set(langs);
        },
        error: () => {
          this.adGroupLanguageOptions.set([]);
          this.languageLabelsByCode.set(new Map());
        },
      });
  }


  /**
   * Load Standard CTAs
   * Docs: https://business-api.tiktok.com/portal/docs/cta-recommendations/v1.3
   */
  loadCTAStandard() {
    const businessAccountId = this.currentUserService.focussedBrand?.id;
    const advertiserId =
      this.selectedAdvertiserAccountId() ?? this.currentSparkCampaign()?.advertiserId;
    if (!businessAccountId || !advertiserId) {
      return;
    }

    this.tiktokSparkAdsService
      .getToolCreativeCtaRecommend({
        businessAccountId,
        advertiserId,
        // Standard CTA list for Spark Ads.
        assetType: 'VIDEO',
        contentType: 'STANDARD',
      })
      .pipe(take(1))
      .subscribe({
        next: (res: TiktokMarketingApiEnvelope) => {
          console.log('loadCTAStandard() ---> res: ', res);
        },
        error: (err: unknown) => {
          console.error('loadCTAStandard() ---> error: ', err);
        },
      });
  }
}
